/* global JSX */
// ListLine
// ***********************************************************************************************************************
import { Box, Card, CardContent, Grid, alpha } from "@mui/material";
import * as React from "react";
import { Base } from "ui/Scripts/source/framework/base";
import { IListCardRowData, ListCardRow } from "../ListCardRow";
import { IListHeaderColumnData, IListValueColumnData, ListHeaderColumn, ListValueColumn } from "../ListColumn";
import * as Styled from "./ListLine.styles";

// ListHeaderLine
export interface IListHeaderLineData {
    classes?: string;
    defaultChecked?: boolean;
    columns: IListHeaderColumnData[];
}

export interface IListHeaderLineProp {
    classes?: string;
    sortColumn?: string;
    sortOrderIsAsc?: boolean;
    defaultChecked?: boolean;
    columns: IListHeaderColumnData[];
    multiSelectCheckBoxColIndex?: number;
    onClick?: (column: string) => void;
    onCheckboxChange?: (column: string, checked: boolean) => void;
}

export class ListHeaderLine extends React.Component<IListHeaderLineProp, {}> {
    render() {
        const props = this.props;
        const multiSelectCheckBoxColIndex = props.multiSelectCheckBoxColIndex ?? 0;
        return (
            <Grid container sx={{ backgroundColor: "action.hover", borderBottom: "1px solid", borderColor: "divider", breakInside: "avoid" }}>
                {props.columns.filter(i => !i.hidden).map((column, i) =>
                    <ListHeaderColumn
                        key={i}
                        {...column}
                        sortColumn={props.sortColumn}
                        sortOrderIsAsc={props.sortOrderIsAsc}
                        defaultChecked={i === 0 ? props.defaultChecked : null}
                        onClick={props.onClick}
                        padding={!props.onCheckboxChange ? "9px 0" : 0}
                        onCheckboxChange={i === multiSelectCheckBoxColIndex ? props.onCheckboxChange : null}
                    />
                )}
            </Grid>
        );
    }
}

// ListValueLine
export interface IListValueLineData {
    classes?: string;
    columns: IListValueColumnData[];
    details?: JSX.Element;
    hoverElement?: JSX.Element;
    id?: string;
    unselectable?: boolean;
    style?: any;
    totalLine?: boolean;
    disableHoverAndSelectedColors?: boolean;
}

export interface IListValueLineProp extends IListValueLineData {
    observer: IntersectionObserver;
    selected?: boolean;
    checked?: boolean;
    multiSelectCheckBoxColIndex?: number;
    onCheckboxChange?: (column: string, checked: boolean) => void;
    onClick?: (id: string, isShift: boolean, isCtrl: boolean) => void;
    onDoubleClick?: (id: string) => void;
    onLongPress?: (id: string) => void;
}

export interface IListValueLineState {
    hovered: boolean;
}

export class ListValueLine extends React.Component<IListValueLineProp, IListValueLineState> {
    private lineDiv = React.createRef<HTMLDivElement>();
    private pressTimer: number = 0;
    private startY: number = 0;
    private clickTimer: number = 0;
    
    constructor(props) {
        super(props);
        this.state = {
            hovered: false
        };
    }

    componentDidMount(): void {
        const props = this.props;
        if (!props.observer || !this.lineDiv.current) return;
        props.observer.observe(this.lineDiv.current);
    }

    componentWillUnmount(): void {
        const props = this.props;
        if (!props.observer || !this.lineDiv.current) return;
        props.observer.unobserve(this.lineDiv.current);
    }

    clearPressTimer = () => {
        clearTimeout(this.pressTimer);
        this.pressTimer = 0;
    };

    handleLinePress = (ev) => {
        const obj = this;
        // Get coordinates
        let y: number;
        if (ev.targetTouches) {
            // Prefer Touch Events
            y = ev.targetTouches[0].clientY;
        } else {
            // Either Mouse event or Pointer Event
            y = ev.clientY;
        }
        this.startY = y;
        // Set timer
        this.pressTimer = window.setTimeout(() => {
            // long press
            obj.clearPressTimer();
            obj.props.onLongPress(this.props.id);
        }, appConfig.longPressMs);
    };

    handleLineRelease = (ev) => {
        // short press
        if (this.pressTimer > 0) {
            this.clearPressTimer();
            // Calculate finger movement
            let lastY = 0;
            if (ev.changedTouches && ev.changedTouches.length > 0) {
                lastY = ev.changedTouches[ev.changedTouches.length - 1].clientY;
            } else if (ev.clientX) {
                lastY = ev.clientY;
            }
            const yDiff = this.startY - lastY;
            if (Math.abs(yDiff) < 10) {
                if (this.props.unselectable) return;
                this.props.onClick(this.props.id, ev.shiftKey, ev.ctrlKey || ev.metaKey);
            }
        }
    };

    clearClickTimer = () => {
        clearTimeout(this.clickTimer);
        this.clickTimer = 0;
    };

    handleClick = (ev) => {
        const props = this.props;
        if (props.unselectable) return;
        if (!props.onDoubleClick) {
            props.onClick(props.id, ev.shiftKey, ev.ctrlKey || ev.metaKey);
            return;
        }
        if (this.clickTimer > 0) {
            this.clearClickTimer();
            props.onDoubleClick(props.id);
            return;
        }
        this.clickTimer = window.setTimeout(() => {
            this.clearClickTimer();
            props.onClick(props.id, ev.shiftKey, ev.ctrlKey || ev.metaKey);
        }, 300);
    };
    
    render() {
        const props = this.props;
        const lineClass = "row valueLine line" + (props.selected ? " selected" : "") + (props.hoverElement ? " hoverElement" : "") + (this.state.hovered ? " hovered" : "") +
            (props.classes ? " " + props.classes : "") + (props.unselectable ? " unselectable" : "");
        const disabled = lineClass.includes("disabled");
        const notNullColumns = props.columns.filter(i => !Base.isNullOrUndefined(i));
        const multiSelectCheckBoxColIndex = props.multiSelectCheckBoxColIndex ?? 0;
        if (!props.onLongPress) {
            const visibleColumns = notNullColumns.filter(i => !i?.hidden);
            return (
                <Box
                    ref={this.lineDiv}
                    data-id={props.id}
                    onClick={this.handleClick}
                    onMouseEnter={() => this.setState({ hovered: true })}
                    onMouseLeave={() => this.setState({ hovered: false })}
                    sx={(theme) => ({
                        "borderBottom": !props.disableHoverAndSelectedColors
                            ? "1px solid"
                            : undefined,
                        "borderTop": props.totalLine ? "1px solid" : "none",
                        "borderColor": theme.palette.divider,
                        "padding": !props.disableHoverAndSelectedColors && !props.onCheckboxChange
                            ? "8.5px 0"
                            : 0,
                        "background": !props.disableHoverAndSelectedColors
                            ? (!props.totalLine && (disabled || props.unselectable)
                                ? theme.palette.action.hover
                                : props.selected
                                    ? alpha(
                                        theme.palette.primary.main,
                                        theme.palette.action.selectedOpacity
                                    )
                                    : undefined
                            )
                            : undefined,
                        "opacity": !props.totalLine && (disabled || props.unselectable)
                            ? 0.75
                            : 1,
                        "display": "flex",
                        "flexWrap": "wrap",
                        "cursor": !props.unselectable
                            ? "pointer"
                            : "default",
                        "position": "relative",
                        "textDecoration": disabled ? "line-through" : "none",
                        "&:hover": {
                            "background": !props.disableHoverAndSelectedColors
                                ? (props.selected
                                    ? alpha(
                                        theme.palette.primary.main,
                                        theme.palette.action.selectedOpacity +
                                                theme.palette.action.hoverOpacity
                                    )
                                    : theme.palette.action.hover)
                                : undefined
                        },
                        "breakInside": "avoid",
                        ...props.style
                    })}
                >
                    <Grid container>
                        {visibleColumns.map((column, i) =>
                            <ListValueColumn
                                {...column}
                                id={props.id}
                                checked={props.checked}
                                disabled={!!visibleColumns.find(j => j.errors && j.errors.length > 0)}
                                key={i}
                                onCheckboxChange={i === multiSelectCheckBoxColIndex ? props.onCheckboxChange : null}
                            />
                        )}
                        {this.state.hovered && props.hoverElement &&
                            <>
                                {props.hoverElement}
                            </>
                        }
                    </Grid>
                    {props.details &&
                        <Card onClick={(e) => e.stopPropagation()} sx={{ width: "100%", margin: "10px 10px 0 10px" }}>
                            <CardContent>
                                {props.details}
                            </CardContent>
                        </Card>
                    }
                </Box>
            );
        } else {
            return (
                <Box
                    ref={this.lineDiv}
                    data-id={props.id}
                    onTouchStart={this.handleLinePress}
                    onTouchEnd={this.handleLineRelease}
                    onMouseDown={this.handleLinePress}
                    onMouseUp={this.handleLineRelease}
                    onMouseEnter={() => this.setState({ hovered: true })}
                    onMouseLeave={(ev) => {
                        this.handleLineRelease(ev);
                        this.setState({ hovered: false });
                    }}
                    sx={(theme) => ({
                        "borderBottom": "1px solid",
                        "borderColor": theme.palette.divider,
                        "padding": 0,
                        "background": disabled || props.unselectable
                            ? theme.palette.action.hover
                            : undefined,
                        "display": "flex",
                        "flexWrap": "wrap",
                        "cursor": "pointer",
                        "position": "relative",
                        "textDecoration": disabled ? "line-through" : "none",
                        "&:hover": {
                            "background": theme.palette.action.hover
                        },
                        "breakInside": "avoid",
                    })}
                >
                    {notNullColumns.map((column, i) =>
                        <ListValueColumn
                            {...column}
                            id={props.id}
                            checked={props.checked}
                            disabled={!!notNullColumns.find(i => i.errors && i.errors.length > 0)}
                            key={i}
                            padding={props.onCheckboxChange ? 0 : "9px 0"}
                            onCheckboxChange={i === multiSelectCheckBoxColIndex ? props.onCheckboxChange : null}
                        />
                    )}
                    {this.state.hovered && props.hoverElement &&
                        <>
                            {props.hoverElement}
                        </>
                    }
                    {props.details &&
                        <Card onClick={(e) => e.stopPropagation()} sx={{ width: "100%", margin: "10px 1rem 0 1rem" }}>
                            <CardContent>
                                {props.details}
                            </CardContent>
                        </Card>
                    }
                </Box>
            );
        }
    }
}

// ListCard
export interface IListCardData {
    sm?: number;
    rows: IListCardRowData[];
    id?: string;
    subTitle?: string;
}

export interface IListCardProp extends IListCardData {
    selected?: boolean;
    checked?: boolean;
    onCheckboxChange?: (column: string, checked: boolean) => void;
    onClick?: (id: string) => void;
    onDoubleClick?: (id: string) => void;
    onLongPress?: (id: string) => void;
}

export class ListCard extends React.Component<IListCardProp, {}> {
    private pressTimer: number = 0;
    private startY: number = 0;

    clearPressTimer = () => {
        clearTimeout(this.pressTimer);
        this.pressTimer = 0;
    };

    handleLinePress = (ev) => {
        const obj = this;
        // Get coordinates
        let y: number;
        if (ev.targetTouches) {
            // Prefer Touch Events
            y = ev.targetTouches[0].clientY;
        } else {
            // Either Mouse event or Pointer Event
            y = ev.clientY;
        }
        this.startY = y;
        // Set timer
        this.pressTimer = window.setTimeout(() => {
            // long press
            obj.clearPressTimer();
            obj.props.onLongPress(this.props.id);
        }, appConfig.longPressMs);
    };

    handleLineRelease = (ev) => {
        // short press
        if (this.pressTimer > 0) {
            this.clearPressTimer();
            // Calculate finger movement
            let lastY = 0;
            if (ev.changedTouches && ev.changedTouches.length > 0) {
                lastY = ev.changedTouches[ev.changedTouches.length - 1].clientY;
            } else if (ev.clientX) {
                lastY = ev.clientY;
            }
            const yDiff = this.startY - lastY;
            if (Math.abs(yDiff) < 10) {
                this.props.onClick(this.props.id);
            }
        }
    };
    
    render() {
        const props = this.props;
        if (props.subTitle) {
            return (
                <Box
                    sx={(theme) => ({
                        "marginTop": "1rem",
                        "borderTop": "1px solid",
                        "borderBottom": "1px solid",
                        "borderColor": theme.palette.divider,
                        "padding": "9px",
                        "paddingLeft": "15px",
                        "background": theme.palette.action.hover,
                        "cursor": "default",
                        "position": "relative",
                        "width": "100%"
                    })}
                >
                    {props.subTitle}
                </Box>
            );
        }
        const validRows = props.rows.filter(i => !Base.isNullOrUndefined(i));
        if (!props.onLongPress) {
            return (
                <Grid
                    data-id={props.id}
                    item
                    sm={props.sm}
                >
                    <Styled.CardContainer
                        elevation={3}
                        selected={props.selected}
                        onClick={props.onClick ? () => props.onClick(props.id) : null}
                        onDoubleClick={props.onDoubleClick ? () => props.onDoubleClick(props.id) : null}
                    >
                        <CardContent>
                            {validRows.map((row, i) =>
                                <ListCardRow
                                    {...row}
                                    id={props.id}
                                    checked={props.checked}
                                    key={i}
                                    index={i}
                                    onCheckboxChange={i === 0 ? props.onCheckboxChange : null}
                                />
                            )}
                        </CardContent>
                    </Styled.CardContainer>
                </Grid>
            );
        } else {
            return (
                <Styled.CardContainer
                    data-id={props.id}
                    elevation={3}
                    selected={props.selected}
                    onTouchStart={this.handleLinePress}
                    onTouchEnd={this.handleLineRelease}
                    onMouseDown={this.handleLinePress}
                    onMouseUp={this.handleLineRelease}
                    onMouseLeave={this.handleLineRelease}>
                    {validRows.map((row, i) =>
                        <ListCardRow
                            {...row}
                            id={props.id}
                            checked={props.checked}
                            key={i}
                            index={i}
                            onCheckboxChange={i === 0 ? props.onCheckboxChange : null}
                        />
                    )}
                </Styled.CardContainer>
            );
        }
    }
}